/**
 * Utility functions for inserting Markdown syntax in textarea
 */

interface InsertResult {
  newValue: string
  cursorPos: number
}

/**
 * Wraps selected text with before/after syntax (e.g., **bold**)
 * If no text is selected, inserts placeholder text
 */
export function insertMarkdown(
  textarea: HTMLTextAreaElement,
  before: string,
  after: string = before,
  placeholder: string = 'testo'
): InsertResult {
  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const value = textarea.value
  const selectedText = value.substring(start, end)

  const textToWrap = selectedText || placeholder
  const newText = before + textToWrap + after

  const newValue = value.substring(0, start) + newText + value.substring(end)

  // Position cursor after the inserted text, or select the placeholder
  const cursorPos = selectedText
    ? start + newText.length
    : start + before.length + placeholder.length

  return { newValue, cursorPos }
}

/**
 * Inserts a prefix at the beginning of the current line (e.g., "- " for lists)
 */
export function insertLinePrefix(
  textarea: HTMLTextAreaElement,
  prefix: string
): InsertResult {
  const start = textarea.selectionStart
  const value = textarea.value

  // Find the start of the current line
  let lineStart = start
  while (lineStart > 0 && value[lineStart - 1] !== '\n') {
    lineStart--
  }

  // Check if prefix already exists at line start
  const lineContent = value.substring(lineStart)
  if (lineContent.startsWith(prefix)) {
    // Remove the prefix if it's already there (toggle behavior)
    const newValue = value.substring(0, lineStart) + lineContent.substring(prefix.length)
    return { newValue, cursorPos: start - prefix.length }
  }

  // Insert prefix at line start
  const newValue = value.substring(0, lineStart) + prefix + value.substring(lineStart)
  const cursorPos = start + prefix.length

  return { newValue, cursorPos }
}

/**
 * Inserts a code block with optional language
 */
export function insertCodeBlock(
  textarea: HTMLTextAreaElement,
  language: string = ''
): InsertResult {
  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const value = textarea.value
  const selectedText = value.substring(start, end)

  const codeContent = selectedText || 'codice'
  const newText = `\`\`\`${language}\n${codeContent}\n\`\`\``

  const newValue = value.substring(0, start) + newText + value.substring(end)
  const cursorPos = start + 3 + language.length + 1 + codeContent.length

  return { newValue, cursorPos }
}

/**
 * Inserts a horizontal rule
 */
export function insertHorizontalRule(textarea: HTMLTextAreaElement): InsertResult {
  const start = textarea.selectionStart
  const value = textarea.value

  // Add newlines if needed
  let prefix = ''
  if (start > 0 && value[start - 1] !== '\n') {
    prefix = '\n'
  }

  const rule = `${prefix}\n---\n\n`
  const newValue = value.substring(0, start) + rule + value.substring(start)
  const cursorPos = start + rule.length

  return { newValue, cursorPos }
}

/**
 * Helper to apply result to textarea and update state
 */
export function applyMarkdownResult(
  textarea: HTMLTextAreaElement,
  result: InsertResult,
  setValue: (value: string) => void
): void {
  setValue(result.newValue)

  // Restore focus and cursor position after React re-render
  requestAnimationFrame(() => {
    textarea.focus()
    textarea.setSelectionRange(result.cursorPos, result.cursorPos)
  })
}
